import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import Modal from "../../../../ui/Modal";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faUpload,
  faTimes,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { swalError, swalWarning, swalSuccess } from "../../../../../utils/swalAlert";

/**
 * Normalization helper for strings (lowercased, punctuation removed, internal spaces collapsed)
 */
const normalizeText = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ");

/**
 * Normalization helper for roll numbers (trimmed, leading zeros stripped for numeric roll numbers)
 */
const normalizeRollNo = (roll) => {
  const s = String(roll ?? "").trim();
  if (!s) return "";
  const num = parseInt(s, 10);
  if (!isNaN(num) && String(num) === s.replace(/^0+/, "")) {
    return String(num);
  }
  return s.toLowerCase();
};

/**
 * Shared dynamic Excel template generator
 * Generates an .xlsx file pre-filled with currently selected students, marks columns, and feedback columns.
 */
export const generateAndDownloadTemplate = ({
  students = [],
  tasks = [],
  testName = "Test",
}) => {
  try {
    if (!tasks || tasks.length === 0) {
      swalWarning("No manual entry questions found for this test.");
      return false;
    }

    if (!students || students.length === 0) {
      swalWarning("No students available to generate template.");
      return false;
    }

    // Generate column headers (Marks column + Feedback column for each task)
    const questionCols = [];
    tasks.forEach((task) => {
      const qNum = task.questionNumber;
      questionCols.push({
        marksHeader: `${qNum} (Max: ${task.maxScore})`,
        feedbackHeader: `${qNum} Feedback`,
      });
    });

    const headers = [
      "Student Name",
      "Roll Number",
      ...questionCols.flatMap((q) => [q.marksHeader, q.feedbackHeader]),
    ];

    // Generate pre-filled rows
    const dataRows = students.map((student) => {
      const studentName =
        student.studentName ||
        `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
        "";
      const rollNo = student.rollNo || "";

      const row = {
        "Student Name": studentName,
        "Roll Number": rollNo,
      };

      tasks.forEach((task, idx) => {
        const { marksHeader, feedbackHeader } = questionCols[idx];
        const existingMark = student.entries?.[idx]?.marksObtained;
        const existingFeedback = student.entries?.[idx]?.feedback;

        row[marksHeader] =
          existingMark !== "" &&
          existingMark !== null &&
          existingMark !== undefined
            ? existingMark
            : "";

        row[feedbackHeader] = existingFeedback || "";
      });

      return row;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataRows, { header: headers });

    // Auto column widths
    const colWidths = [
      { wch: 26 }, // Student Name
      { wch: 16 }, // Roll Number
    ];
    tasks.forEach(() => {
      colWidths.push({ wch: 18 }); // Marks column
      colWidths.push({ wch: 28 }); // Feedback column
    });
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Marks Entry");

    // Sanitize test name for filename
    const safeTestName = String(testName || "Exam")
      .replace(/[^\w\s-]/gi, "")
      .replace(/\s+/g, "_");
    const filename = `${safeTestName}_Marks_Template.xlsx`;

    XLSX.writeFile(wb, filename);
    swalSuccess(`Downloaded marks template for ${students.length} student(s).`);
    return true;
  } catch (err) {
    console.error("Failed to generate marks template:", err);
    swalError("Failed to generate Excel template. Please try again.");
    return false;
  }
};

/**
 * AutofillMarksModal
 * Enables instructors to upload a scored spreadsheet to auto-fill grades & feedback
 * via two-factor student matching (Name + Roll Number), with template download support.
 */
const AutofillMarksModal = ({
  isOpen,
  onClose,
  students = [],
  tasks = [],
  testName = "Test",
  onAutofill,
}) => {
  const [excelFile, setExcelFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    if (isProcessing) return;
    setExcelFile(null);
    setDragActive(false);
    onClose();
  };

  // Drag & Drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file) => {
    const validExtensions = [".xlsx", ".xls", ".csv"];
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!validExtensions.includes(fileExt)) {
      swalError("Invalid file type. Please upload an Excel (.xlsx, .xls) or CSV file.");
      return;
    }
    setExcelFile(file);
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setExcelFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 3. Process Excel file, perform two-factor student matching, and auto-fill marks + feedback
  const handleProcessFile = async () => {
    if (!excelFile) {
      swalWarning("Please select or drop an Excel file first.");
      return;
    }

    setIsProcessing(true);
    try {
      const arrayBuffer = await excelFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        swalError("No worksheets found in the uploaded file.");
        setIsProcessing(false);
        return;
      }

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

      if (!rawRows || rawRows.length === 0) {
        swalWarning("Uploaded spreadsheet contains no data.");
        setIsProcessing(false);
        return;
      }

      // Find header row (first non-empty row)
      let headerRowIndex = -1;
      for (let i = 0; i < rawRows.length; i++) {
        if (rawRows[i] && rawRows[i].some((cell) => String(cell || "").trim() !== "")) {
          headerRowIndex = i;
          break;
        }
      }

      if (headerRowIndex === -1) {
        swalWarning("No header row found in Excel file.");
        setIsProcessing(false);
        return;
      }

      const headerRow = rawRows[headerRowIndex];
      const dataRows = rawRows.slice(headerRowIndex + 1);

      // Map columns
      let nameColIndex = -1;
      let rollColIndex = -1;
      const taskMarksColMap = {}; // taskIndex -> columnIndex in excel for marks
      const taskFeedbackColMap = {}; // taskIndex -> columnIndex in excel for feedback

      headerRow.forEach((cell, colIdx) => {
        const headerStr = String(cell || "").toLowerCase().trim();

        // Check for student name (and not feedback)
        if (
          nameColIndex === -1 &&
          !headerStr.includes("feedback") &&
          (headerStr === "student name" ||
            headerStr === "name" ||
            headerStr === "full name" ||
            headerStr.includes("student") ||
            headerStr.includes("candidate"))
        ) {
          nameColIndex = colIdx;
        }

        // Check for roll number
        if (
          rollColIndex === -1 &&
          (headerStr.includes("roll") ||
            headerStr.includes("reg") ||
            headerStr === "id" ||
            headerStr === "rollno")
        ) {
          rollColIndex = colIdx;
        }

        // Check for feedback columns matching tasks
        if (headerStr.includes("feedback") || headerStr.includes("comment") || headerStr.includes("remark")) {
          tasks.forEach((task, tIdx) => {
            const qNumStr = String(task.questionNumber).toLowerCase();
            const cleanHeader = headerStr.replace(/\s+/g, "");
            const isMatch =
              cleanHeader.includes(`q${qNumStr}`) ||
              cleanHeader.includes(`question${qNumStr}`) ||
              cleanHeader.includes(`task${qNumStr}`) ||
              cleanHeader.includes(qNumStr) ||
              (tasks.length === 1 && headerStr.includes("feedback"));

            if (isMatch && taskFeedbackColMap[tIdx] === undefined) {
              taskFeedbackColMap[tIdx] = colIdx;
            }
          });
        } else {
          // Check for marks columns matching tasks
          tasks.forEach((task, tIdx) => {
            const qNumStr = String(task.questionNumber).toLowerCase();
            const cleanHeader = headerStr.replace(/\s+/g, "");
            const isMatch =
              cleanHeader.startsWith(`q${qNumStr}`) ||
              cleanHeader.startsWith(`question${qNumStr}`) ||
              cleanHeader.startsWith(`task${qNumStr}`) ||
              cleanHeader === qNumStr ||
              headerStr.match(new RegExp(`\\bq?${qNumStr}\\b`, "i"));

            if (isMatch && taskMarksColMap[tIdx] === undefined) {
              taskMarksColMap[tIdx] = colIdx;
            }
          });
        }
      });

      if (nameColIndex === -1 && rollColIndex === -1) {
        swalError(
          "Could not identify 'Student Name' or 'Roll Number' columns in the uploaded file. Please use the downloaded template."
        );
        setIsProcessing(false);
        return;
      }

      if (Object.keys(taskMarksColMap).length === 0 && Object.keys(taskFeedbackColMap).length === 0) {
        swalError(
          "No matching question or feedback columns found in the uploaded file. Headers should resemble 'Q1', 'Q1 Feedback', etc."
        );
        setIsProcessing(false);
        return;
      }

      // Perform matching and score/feedback extraction
      const matchedUpdates = {}; // studentIndex -> { taskIndex: { marksObtained?: number, feedback?: string } }
      const unmatchedRows = [];
      const scoreWarnings = [];
      const matchedStudentIndices = new Set();
      let totalFeedbackCount = 0;

      dataRows.forEach((row, rowIdx) => {
        // Skip completely empty rows
        if (!row || !row.some((c) => String(c || "").trim() !== "")) return;

        const rowName = nameColIndex !== -1 ? String(row[nameColIndex] || "").trim() : "";
        const rowRoll = rollColIndex !== -1 ? String(row[rollColIndex] || "").trim() : "";

        if (!rowName && !rowRoll) return;

        const normRowName = normalizeText(rowName);
        const normRowRoll = normalizeRollNo(rowRoll);

        // Strict Two-Factor Matching against modal students
        let matchedIndex = -1;

        for (let sIdx = 0; sIdx < students.length; sIdx++) {
          const student = students[sIdx];
          const studName =
            student.studentName ||
            `${student.firstName || ""} ${student.lastName || ""}`.trim();
          const studRoll = student.rollNo || "";

          const normStudName = normalizeText(studName);
          const normStudRoll = normalizeRollNo(studRoll);

          // Priority 1: If both have Roll Number, BOTH Name and Roll Number must match
          if (normRowRoll && normStudRoll) {
            if (normRowRoll === normStudRoll && normRowName === normStudName) {
              matchedIndex = sIdx;
              break;
            }
          } else if (normRowName && normStudName && normRowName === normStudName) {
            // Priority 2: If roll number is absent on either, match exact normalized Name
            matchedIndex = sIdx;
            break;
          }
        }

        if (matchedIndex !== -1) {
          matchedStudentIndices.add(matchedIndex);
          const studentUpdates = {};
          const currentStudent = students[matchedIndex];
          const studDisplayName =
            currentStudent.studentName ||
            `${currentStudent.firstName || ""} ${currentStudent.lastName || ""}`.trim();

          // 1. Extract marks for mapped task columns
          Object.entries(taskMarksColMap).forEach(([tIdxStr, colIdx]) => {
            const taskIndex = parseInt(tIdxStr, 10);
            const task = tasks[taskIndex];
            const cellVal = row[colIdx];

            if (
              cellVal === "" ||
              cellVal === null ||
              cellVal === undefined ||
              String(cellVal).trim() === "-"
            ) {
              // Leave blank / unchanged
              return;
            }

            const parsedVal = parseFloat(String(cellVal).trim());
            if (isNaN(parsedVal)) {
              return;
            }

            if (parsedVal < 0) {
              scoreWarnings.push(
                `${studDisplayName}: Negative score (${parsedVal}) for ${task.questionNumber} ignored.`
              );
              return;
            }

            let finalMark = parsedVal;
            const taskLabel = task?.questionNumber
              ? (/^\d+$/.test(String(task.questionNumber)) ? `${task.questionNumber}` : String(task.questionNumber))
              : `Task ${taskIndex + 1}`;

            if (finalMark > task.maxScore) {
              scoreWarnings.push(
                `${studDisplayName}: ${taskLabel} score (${parsedVal}) exceeds maximum (${task.maxScore}). Maximum score you can give is up to ${task.maxScore} for this test.`
              );
            }

            // Round to 2 decimal places
            finalMark = Math.round(finalMark * 100) / 100;
            if (!studentUpdates[taskIndex]) studentUpdates[taskIndex] = {};
            studentUpdates[taskIndex].marksObtained = finalMark;
          });

          // 2. Extract feedback for mapped feedback columns
          Object.entries(taskFeedbackColMap).forEach(([tIdxStr, colIdx]) => {
            const taskIndex = parseInt(tIdxStr, 10);
            const cellVal = row[colIdx];

            if (cellVal !== undefined && cellVal !== null) {
              const feedbackStr = String(cellVal).trim();
              if (feedbackStr !== "" && feedbackStr !== "-") {
                if (!studentUpdates[taskIndex]) studentUpdates[taskIndex] = {};
                studentUpdates[taskIndex].feedback = feedbackStr;
                totalFeedbackCount++;
              }
            }
          });

          if (Object.keys(studentUpdates).length > 0) {
            matchedUpdates[matchedIndex] = studentUpdates;
          }
        } else {
          unmatchedRows.push({
            rowNumber: headerRowIndex + 2 + rowIdx,
            name: rowName || "(Empty)",
            roll: rowRoll || "(Empty)",
          });
        }
      });

      const matchedCount = matchedStudentIndices.size;

      if (matchedCount === 0) {
        swalWarning(
          "No student records in the file matched the enrolled students in the modal. Please verify student names and roll numbers using the downloaded template."
        );
        setIsProcessing(false);
        return;
      }

      // Apply marks & feedback to parent bulk table
      onAutofill(matchedUpdates);

      // Build summary feedback
      let summaryHtml = `
        <div style="text-align: left; font-size: 0.95rem;">
          <p style="margin-bottom: 0.75rem; color: #15803d; font-weight: 600;">
            <i class="fa-solid fa-circle-check"></i> Successfully auto-filled marks for <strong>${matchedCount}</strong> out of <strong>${students.length}</strong> student(s).
          </p>
      `;

      if (totalFeedbackCount > 0) {
        summaryHtml += `
          <p style="margin-bottom: 0.75rem; color: #0284c7; font-size: 0.9rem;">
            <i class="fa-solid fa-comment-dots"></i> Imported feedback comments for <strong>${totalFeedbackCount}</strong> question response(s).
          </p>
        `;
      }

      if (unmatchedRows.length > 0) {
        summaryHtml += `
          <div style="margin-top: 0.75rem; padding: 0.6rem 0.75rem; background: #fff1f2; border: 1px solid #fecdd3; border-radius: 6px;">
            <p style="margin: 0 0 0.4rem 0; font-weight: 600; color: #be123c; font-size: 0.85rem;">
              <i class="fa-solid fa-triangle-exclamation"></i> ${unmatchedRows.length} row(s) in Excel did not match any student:
            </p>
            <ul style="margin: 0; padding-left: 1.25rem; font-size: 0.8rem; color: #881337; max-height: 100px; overflow-y: auto;">
              ${unmatchedRows
                .slice(0, 5)
                .map((u) => `<li>Row ${u.rowNumber}: ${u.name} (Roll: ${u.roll})</li>`)
                .join("")}
              ${unmatchedRows.length > 5 ? `<li>...and ${unmatchedRows.length - 5} more</li>` : ""}
            </ul>
          </div>
        `;
      }

      if (scoreWarnings.length > 0) {
        summaryHtml += `
          <div style="margin-top: 0.75rem; padding: 0.6rem 0.75rem; background: #fef2f2; border: 1px solid #fecdd3; border-radius: 6px;">
            <p style="margin: 0 0 0.4rem 0; font-weight: 600; color: #dc2626; font-size: 0.85rem;">
              <i class="fa-solid fa-triangle-exclamation"></i> Score Exceeded Notices (Highlighted in red):
            </p>
            <ul style="margin: 0; padding-left: 1.25rem; font-size: 0.8rem; color: #b91c1c; max-height: 80px; overflow-y: auto;">
              ${scoreWarnings
                .slice(0, 4)
                .map((w) => `<li>${w}</li>`)
                .join("")}
              ${scoreWarnings.length > 4 ? `<li>...and ${scoreWarnings.length - 4} more</li>` : ""}
            </ul>
          </div>
        `;
      }

      summaryHtml += `</div>`;

      Swal.fire({
        icon: "success",
        title: "Marks & Feedback Autofilled!",
        html: summaryHtml,
        confirmButtonText: "Review in Table",
        confirmButtonColor: "var(--color-1, #f09a21)",
      });

      handleClose();
    } catch (err) {
      console.error("Autofill processing error:", err);
      swalError("Failed to parse the Excel file. Please ensure it is a valid .xlsx or .xls file.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="600px">
      <div style={{ padding: "0.5rem" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "10px",
              backgroundColor: "rgba(16, 185, 129, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#10b981",
              fontSize: "1.3rem",
            }}
          >
            <FontAwesomeIcon icon={faFileExcel} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.3rem", fontWeight: "700", color: "#1e293b" }}>
              Autofill Marks &amp; Feedback from Excel
            </h2>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b" }}>
              Upload your spreadsheet to match students and auto-fill marks and question feedback.
            </p>
          </div>
        </div>

        {/* Drag & Drop Zone */}
        <div
          className={`bulk-update-upload-zone ${dragActive ? "drag-active" : ""}`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ margin: "1.25rem 0 0.75rem 0", padding: "1.75rem 1rem" }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            disabled={isProcessing}
          />
          <FontAwesomeIcon icon={faUpload} className="bulk-update-upload-icon" />
          <div className="bulk-update-upload-text">
            <h4>Drag &amp; drop filled Excel file here</h4>
            <p>or click to browse from device (.xlsx, .xls, .csv)</p>
          </div>
        </div>

        {/* Uploaded File Info Preview */}
        {excelFile && (
          <div className="bulk-update-file-info" style={{ marginTop: "0.5rem" }}>
            <div className="bulk-update-file-details">
              <FontAwesomeIcon icon={faFileExcel} style={{ fontSize: "1.1rem", color: "#10b981" }} />
              <span style={{ wordBreak: "break-all" }}>
                {excelFile.name} ({(excelFile.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              type="button"
              className="bulk-update-file-remove"
              onClick={removeFile}
              title="Remove file"
              disabled={isProcessing}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Modal Actions */}
        <div
          style={{
            marginTop: "1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
          }}
        >
          <button
            type="button"
            className="btn-cancel"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="create-btn"
            onClick={handleProcessFile}
            disabled={!excelFile || isProcessing}
            style={{
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "var(--color-3, #10b981)",
            }}
          >
            {isProcessing ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Autofill Marks</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AutofillMarksModal;
